import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import OrderBook from './components/OrderBook';
import OrderForm from './components/OrderForm';
import ActiveOrders from './components/ActiveOrders';
import Matches from './components/Matches';
import MarketTicker from './components/MarketTicker';
import TradingChart from './components/TradingChart';
import Positions from './components/Positions';
import { useInstruments } from './hooks/useInstruments';
import { useClerkAuth } from './hooks/useClerkAuth';

// Frontend Version - Single source of truth
const FRONTEND_VERSION = '2.4.45';

// API and WebSocket base URLs - dynamically determined from current hostname
// This allows the same build to work on different domains/subdomains
function getApiBaseUrl() {
  // Use environment variable if set (for development)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Determine from current hostname
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  
  // Root domain -> root domain API
  if (hostname === 'amberhorizon.eu' || hostname === 'www.amberhorizon.eu') {
    return `${protocol}//amberhorizon.eu`;
  }
  
  // Subdomain -> subdomain API (e.g., hori.amberhorizon.eu -> https://hori.amberhorizon.eu)
  return `${protocol}//${hostname}`;
}

function getWsBaseUrl() {
  // Use environment variable if set (for development)
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  
  // Determine from current hostname
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  
  // Root domain -> root domain WebSocket
  if (hostname === 'amberhorizon.eu' || hostname === 'www.amberhorizon.eu') {
    return `${protocol}//amberhorizon.eu`;
  }
  
  // Subdomain -> subdomain WebSocket
  return `${protocol}//${hostname}`;
}

const API_BASE_URL = getApiBaseUrl();
const WS_BASE_URL = getWsBaseUrl();

/**
 * Utility function to parse UTC timestamp consistently
 */
const parseUTCTimestamp = (timestamp) => {
  const timestampStr = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  return new Date(timestampStr);
};

function App() {
  // Version identifier - will be in production build
  console.log(`Frontend Version: ${FRONTEND_VERSION}`);
  
  // Multi-instrument order book state
  const [visibleInstruments, setVisibleInstruments] = useState([]);
  const orderBooksByInstrumentRef = useRef({});
  const visibleInstrumentsRef = useRef([]);
  const [orderBooksByInstrument, setOrderBooksByInstrument] = useState({});
  const [activeOrders, setActiveOrders] = useState({ orders: [], timestamp: null });
  const [activeOrdersFilter, setActiveOrdersFilter] = useState('ALL');
  const [selectedOrderInstrument, setSelectedOrderInstrument] = useState(null); // Instrument for order entry
  const [matches, setMatches] = useState([]);
  const [position, setPosition] = useState(null);
  const [wsStatus, setWsStatus] = useState('connecting');
  const [activeOrdersWsStatus, setActiveOrdersWsStatus] = useState('connecting');
  const [lastUpdate, setLastUpdate] = useState('—');
  const [depth, setDepth] = useState(10);
  const [socket, setSocket] = useState(null);
  const [activeOrdersSocket, setActiveOrdersSocket] = useState(null);
  const [status, setStatus] = useState('');
  const [modifyModal, setModifyModal] = useState({ show: false, order: null, position: { x: 0, y: 0 } });
  
  // Keep refs in sync with state
  useEffect(() => {
    visibleInstrumentsRef.current = visibleInstruments;
  }, [visibleInstruments]);
  
  useEffect(() => {
    orderBooksByInstrumentRef.current = orderBooksByInstrument;
  }, [orderBooksByInstrument]);
  
  // Clerk authentication - use email username (part before @) as trading user_id
  const { userId, userEmail, clerkUserId, isAuthenticated, isLoading: authLoading, isProcessingSession, signIn, signOut } = useClerkAuth();
  
  // Access control state
  const [accessCheckLoading, setAccessCheckLoading] = useState(false);
  const [accessCheckError, setAccessCheckError] = useState(null);
  const [hasAccess, setHasAccess] = useState(null); // null = not checked yet, true/false = checked
  
  // Chart state
  const [availableInstruments, setAvailableInstruments] = useState([]);
  const [selectedChartInstrument, setSelectedChartInstrument] = useState('');
  const [timeframe, setTimeframe] = useState('1h');
  
  // Load instruments dynamically
  const {
    loading: instrumentsLoading,
    error: instrumentsError,
    defaultInstrument,
    getTickSize,
    validatePrice,
    roundToTick,
    getSymbols
  } = useInstruments(API_BASE_URL);
  
  // Memoize all instruments list for MarketTicker to prevent unnecessary re-renders
  const allInstrumentsList = useMemo(() => {
    return getSymbols ? getSymbols() : [];
  }, [getSymbols]);
  
  // Sync state with defaultInstrument after it's loaded (moved here to avoid hoisting issue)
  useEffect(() => {
    visibleInstrumentsRef.current = visibleInstruments;
    // If state is empty but we have a defaultInstrument, restore it
    if (visibleInstruments.length === 0 && defaultInstrument) {
      setVisibleInstruments([defaultInstrument]);
    }
  }, [visibleInstruments, defaultInstrument]);
  
  // Load order book for a specific instrument (defined early to avoid hoisting issues)
  const loadBookForInstrument = useCallback(async (symbol) => {
    try {
      const data = await fetchJSON(`${API_BASE_URL}/api/market_data?symbol=${symbol}`);
      const normalized = normalizeBook(data);
      setOrderBooksByInstrument(prev => ({
        ...prev,
        [symbol]: normalized
      }));
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (error) {
      console.error(`Failed to load order book for ${symbol}:`, error);
    }
  }, []);

  // Initialize visible instruments with defaultInstrument when it's loaded
  // Use a ref to track if we've already initialized to prevent resets
  const initializedRef = useRef(false);
  
  useEffect(() => {
    if (defaultInstrument && !initializedRef.current) {
      // Initialize visible instruments with defaultInstrument
      setVisibleInstruments([defaultInstrument]);
      
      // Initialize selected order instrument
      setSelectedOrderInstrument(defaultInstrument);
      
      // Initialize order book for default instrument
      setOrderBooksByInstrument({
        [defaultInstrument]: { bids: [], asks: [], last_traded_price: null }
      });
      
      // Mark as initialized
      initializedRef.current = true;
    }
  }, [defaultInstrument]); // Only depend on defaultInstrument
  
  // Load initial market data when visibleInstruments is first populated
  useEffect(() => {
    if (visibleInstruments.length > 0) {
      // Check if we need to load data for any visible instrument
      const needsLoad = visibleInstruments.some(symbol => {
        const book = orderBooksByInstrument[symbol];
        return !book || (book.bids.length === 0 && book.asks.length === 0 && book.last_traded_price === null);
      });
      
      if (needsLoad) {
        // Load market data for all visible instruments that don't have data yet
        visibleInstruments.forEach(symbol => {
          const book = orderBooksByInstrument[symbol];
          if (!book || (book.bids.length === 0 && book.asks.length === 0 && book.last_traded_price === null)) {
            loadBookForInstrument(symbol);
          }
        });
      }
    }
  }, [visibleInstruments, loadBookForInstrument]); // Removed orderBooksByInstrument from deps to avoid infinite loop
  
  // Update selected order instrument when visible instruments change
  useEffect(() => {
    if (selectedOrderInstrument && !visibleInstruments.includes(selectedOrderInstrument)) {
      // If selected instrument was removed, switch to first visible instrument
      setSelectedOrderInstrument(visibleInstruments[0] || defaultInstrument);
    } else if (!selectedOrderInstrument && visibleInstruments.length > 0) {
      // If no selection, use first visible instrument
      setSelectedOrderInstrument(visibleInstruments[0]);
    }
  }, [visibleInstruments, selectedOrderInstrument, defaultInstrument]);

  // Instrument management functions
  const addInstrument = useCallback((symbol) => {
    if (!symbol || visibleInstruments.includes(symbol)) return;
    setVisibleInstruments(prev => [...prev, symbol]);
    setOrderBooksByInstrument(prev => ({
      ...prev,
      [symbol]: { bids: [], asks: [], last_traded_price: null }
    }));
    // Load initial market data for the new instrument
    loadBookForInstrument(symbol);
  }, [visibleInstruments, loadBookForInstrument]);

  const removeInstrument = useCallback((symbol) => {
    if (visibleInstruments.length <= 1) {
      setStatus('⚠️ Cannot remove the last instrument');
      return;
    }
    setVisibleInstruments(prev => prev.filter(s => s !== symbol));
    setOrderBooksByInstrument(prev => {
      const updated = { ...prev };
      delete updated[symbol];
      return updated;
    });
  }, [visibleInstruments.length]);

  // Load order book data for all visible instruments
  const loadBook = useCallback(async () => {
    // Load for all visible instruments
    visibleInstruments.forEach(symbol => {
      loadBookForInstrument(symbol);
    });
  }, [visibleInstruments, loadBookForInstrument]);

  // Utility function to fetch JSON data
  const fetchJSON = async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });
      
      if (!response.ok) {
        // Try to extract error detail from response body
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = `${errorMessage}: ${response.statusText}`;
          }
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = `${errorMessage}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  };

  // Normalize order book data
  const normalizeBook = (data) => {
    if (!data || !data.bids || !data.asks) {
      // Return empty arrays instead of current state to avoid circular reference
      return { bids: [], asks: [], last_traded_price: null };
    }
    
    return {
      bids: Array.isArray(data.bids) ? data.bids : [],
      asks: Array.isArray(data.asks) ? data.asks : [],
      last_traded_price: data.last_traded_price || null
    };
  };


  // Load active orders data for specific user
  const loadActiveOrders = useCallback(async (userIdToLoad = null) => {
    const targetUserId = userIdToLoad || userId;
    try {
      const data = await fetchJSON(`${API_BASE_URL}/api/active_orders/${targetUserId}`);
      setActiveOrders({
        orders: Array.isArray(data.orders) ? data.orders : [],
        timestamp: data.timestamp || null
      });
    } catch (error) {
      console.error('Failed to load active orders:', error);
    }
  }, [userId]);

  // Load position data for specific user
  const loadPosition = useCallback(async (userIdToLoad = null, symbolToLoad = null) => {
    const targetUserId = userIdToLoad || userId;
    const targetSymbol = symbolToLoad || defaultInstrument;
    if (!targetUserId) return;
    
    try {
      const url = `${API_BASE_URL}/api/positions/${targetUserId}${targetSymbol ? `?symbol=${targetSymbol}` : ''}`;
      const data = await fetchJSON(url);
      setPosition(data);
    } catch (error) {
      console.error('Failed to load position:', error);
      setPosition(null);
    }
  }, [userId, defaultInstrument]);

  // Load matches data (fetch once, filter client-side by timeframe)
  const loadMatches = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API_BASE_URL}/api/matches/graph`);
      // API returns {matches: [...], timestamp: "...", total_matches: N}
      const matchesData = Array.isArray(data) ? data : (data.matches || []);
      
      // Sort matches by timestamp, newest first
      const sortedMatches = [...matchesData].sort((a, b) => {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        return timeB - timeA; // Newest first
      });
      
      setMatches(sortedMatches);
      
      // Transform data for chart (all data, will be filtered by timeframe in TradingChart)
      if (sortedMatches.length > 0) {
        // Extract available instruments (filter out UNKNOWN)
        const instruments = [...new Set(sortedMatches.map(match => match.instrument || 'AMBER'))]
          .filter(instrument => instrument !== 'UNKNOWN');
        
        setAvailableInstruments(instruments);
        
        // Auto-select the first instrument or keep current selection if valid
        setSelectedChartInstrument(prev => {
          if (prev && instruments.includes(prev)) return prev;
          return instruments[0] || 'AMBER';
        });
        
        // Chart will automatically use the matches data
      }
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  }, []);

  // WebSocket connection
  useEffect(() => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    
    const connectWS = () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        return;
      }
      
      const wsUrl = `${WS_BASE_URL}/ws/market_data`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setWsStatus('live');
        reconnectAttempts = 0;
        
        // Only load data on initial connection, not on reconnects
        if (reconnectAttempts === 0) {
          loadBook();
          loadMatches();
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'book' && data.data) {
            // Extract symbol from market data (check top-level symbol first, then bids/asks)
            let symbol = data.data.symbol;
            if (!symbol && data.data.bids && data.data.bids.length > 0) {
              symbol = data.data.bids[0]?.symbol;
            }
            if (!symbol && data.data.asks && data.data.asks.length > 0) {
              symbol = data.data.asks[0]?.symbol;
            }
            
            // If still no symbol, try to infer from visible instruments (for initial load)
            if (!symbol && visibleInstruments.length === 1) {
              symbol = visibleInstruments[0];
            }
            
            // Use ref to get current value (avoids stale closure)
            const currentVisibleInstruments = visibleInstrumentsRef.current;
            
            // Check if visibleInstruments is empty and try to fix it
            if (currentVisibleInstruments.length === 0 && defaultInstrument) {
              setVisibleInstruments([defaultInstrument]);
              visibleInstrumentsRef.current = [defaultInstrument];
              // Don't return - continue processing with the restored symbol
              if (!symbol) {
                symbol = defaultInstrument;
              }
            }
            
            // Also check if state is empty but ref has value - sync them
            if (visibleInstruments.length === 0 && currentVisibleInstruments.length > 0) {
              setVisibleInstruments(currentVisibleInstruments);
            }
            
            // Use ref value for comparison
            if (symbol && currentVisibleInstruments.includes(symbol)) {
              const normalized = normalizeBook(data.data);
              
              // Filter out any orders with invalid status or zero remaining quantity
              const validBids = normalized.bids.filter(order => 
                order.status !== 'cancelled' && 
                (order.remaining_quantity ?? order.quantity ?? 0) > 0
              );
              const validAsks = normalized.asks.filter(order => 
                order.status !== 'cancelled' && 
                (order.remaining_quantity ?? order.quantity ?? 0) > 0
              );
              
              // Update order book for this specific instrument with filtered data
              setOrderBooksByInstrument(prev => ({
                ...prev,
                [symbol]: {
                  ...normalized,
                  bids: validBids,
                  asks: validAsks
                }
              }));
              setLastUpdate(new Date().toLocaleTimeString());
            }
          } else if (data.type === 'match' && data.data) {
            // Create new chart data point for the chart
            const newMatch = {
              ...data.data,
              time: parseUTCTimestamp(data.data.timestamp).toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit' 
              }),
              price: data.data.price,
              volume: data.data.quantity,
              timestamp: data.data.timestamp,
              instrument: data.data.instrument || 'AMBER'
            };
            
            // Update matches array (keep only last 100 to prevent memory issues)
            setMatches(prev => {
              const updated = [newMatch, ...prev];
              return updated.slice(0, 100); // Keep only last 100 matches
            });
            
            // Update available instruments if this is a new instrument
            const instrument = data.data.instrument || 'AMBER';
            setAvailableInstruments(prev => {
              if (!prev.includes(instrument)) {
                return [...prev, instrument];
              }
              return prev;
            });
          } else if (data.bids && data.asks) {
            // Handle direct order book data (fallback for different message format)
            let symbol = data.symbol;
            if (!symbol) {
              symbol = data.bids?.[0]?.symbol || data.asks?.[0]?.symbol;
            }
            
            // If still no symbol, try to infer from visible instruments (for initial load)
            if (!symbol && visibleInstruments.length === 1) {
              symbol = visibleInstruments[0];
            }
            
            if (symbol && visibleInstruments.includes(symbol)) {
              const normalized = normalizeBook(data);
              setOrderBooksByInstrument(prev => ({
                ...prev,
                [symbol]: normalized
              }));
              setLastUpdate(new Date().toLocaleTimeString());
            }
          }
        } catch (error) {
          console.error('WebSocket message error:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error(`❌ [WebSocket] Error connecting to ${wsUrl}:`, error);
        setWsStatus('error');
      };
      
      ws.onclose = (event) => {
        console.log(`⚠️ [WebSocket] Closed connection to ${wsUrl}, code: ${event.code}, reason: ${event.reason}`);
        setWsStatus('reconnecting');
        
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          setTimeout(() => {
            connectWS();
          }, 2000 * reconnectAttempts);
        } else {
          setWsStatus('error');
        }
      };
      
      setSocket(ws);
    };
    
    connectWS();
    
    return () => {
      if (socket) {
        socket.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  // Periodic refresh (only when WebSocket is not live)
  useEffect(() => {
    if (wsStatus === 'live') {
      return; // Don't refresh when WebSocket is live
    }
    
    const interval = setInterval(() => {
      loadBook();
      loadMatches();
    }, 60000); // 60 seconds
    
    return () => clearInterval(interval);
  }, [wsStatus, loadBook, loadMatches]);

  // WebSocket connection for active orders
  useEffect(() => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    
    const connectActiveOrdersWS = () => {
      if (activeOrdersSocket && activeOrdersSocket.readyState === WebSocket.OPEN) {
        console.log('🔍 DEBUG: WebSocket already open, skipping connection');
        return;
      }
      
      const wsUrl = `${WS_BASE_URL}/ws/active_orders/${userId}`;
      console.log('🔍 DEBUG: Connecting to active orders WebSocket:', wsUrl);
      console.log('🔍 DEBUG: userId=', userId, 'type=', typeof userId, 'length=', userId?.length);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('🔍 DEBUG: Active orders WebSocket opened successfully');
        console.log('🔍 DEBUG: WebSocket URL:', wsUrl);
        console.log('🔍 DEBUG: WebSocket readyState:', ws.readyState);
        setActiveOrdersWsStatus('live');
        reconnectAttempts = 0;
      };
      
      ws.onmessage = (event) => {
        console.log('🔍 DEBUG: Active orders WebSocket message received');
        console.log('🔍 DEBUG: Message data length:', event.data?.length);
        console.log('🔍 DEBUG: Raw message data:', event.data);
        
        try {
          const data = JSON.parse(event.data);
          console.log('🔍 DEBUG: Parsed message data:', data);
          console.log('🔍 DEBUG: Message type:', data.type);
          console.log('🔍 DEBUG: Orders is array?', Array.isArray(data.orders));
          console.log('🔍 DEBUG: Orders count:', data.orders?.length);
          
          if (data.type === 'active_orders' && Array.isArray(data.orders)) {
            console.log('🔍 DEBUG: Setting active orders state with', data.orders.length, 'orders');
            console.log('🔍 DEBUG: Orders data:', JSON.stringify(data.orders, null, 2));
            setActiveOrders({
              orders: data.orders,
              timestamp: data.timestamp || null
            });
            console.log('🔍 DEBUG: Active orders state updated successfully');
          } else {
            console.warn('🔍 DEBUG: Message does not match expected format. type=', data.type, 'orders is array?', Array.isArray(data.orders));
          }
        } catch (error) {
          console.error('🔍 DEBUG: Active orders WebSocket message error:', error);
          console.error('🔍 DEBUG: Error parsing message data:', event.data);
        }
      };
      
      ws.onerror = (error) => {
        console.error('🔍 DEBUG: Active orders WebSocket error:', error);
        console.error('🔍 DEBUG: WebSocket URL:', wsUrl);
        console.error('🔍 DEBUG: WebSocket readyState:', ws.readyState);
        setActiveOrdersWsStatus('error');
      };
      
      ws.onclose = (event) => {
        console.log('🔍 DEBUG: Active orders WebSocket closed');
        console.log('🔍 DEBUG: Close code:', event.code);
        console.log('🔍 DEBUG: Close reason:', event.reason);
        console.log('🔍 DEBUG: Was clean?', event.wasClean);
        setActiveOrdersWsStatus('connecting');
        reconnectAttempts++;
        
        if (reconnectAttempts < maxReconnectAttempts) {
          console.log('🔍 DEBUG: Reconnecting in 3 seconds (attempt', reconnectAttempts, 'of', maxReconnectAttempts, ')');
          setTimeout(connectActiveOrdersWS, 3000);
        } else {
          console.error('🔍 DEBUG: Max reconnection attempts reached');
          setActiveOrdersWsStatus('failed');
        }
      };
      
      setActiveOrdersSocket(ws);
      console.log('🔍 DEBUG: WebSocket object created and stored');
    };
    
    if (userId) {
      console.log('🔍 DEBUG: userId available, connecting WebSocket. userId=', userId);
      connectActiveOrdersWS();
    } else {
      console.warn('🔍 DEBUG: userId not available, skipping WebSocket connection');
    }
    
    return () => {
      console.log('🔍 DEBUG: Cleaning up active orders WebSocket connection');
      if (activeOrdersSocket) {
        activeOrdersSocket.close();
        console.log('🔍 DEBUG: Active orders WebSocket closed in cleanup');
      }
    };
  }, [userId]); // Reconnect when userId changes

  // Initial load on component mount
  useEffect(() => {
    loadBook();
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Load user-specific data when userId becomes available
  useEffect(() => {
    if (userId) {
      console.log('🔄 [App] Loading user data for:', userId);
      loadActiveOrders();
      loadPosition(); // Load position immediately when userId is available
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Reload when userId changes

  // Periodically refresh position (every 30 seconds)
  useEffect(() => {
    if (!userId) return;
    
    const interval = setInterval(() => {
      loadPosition();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [userId, loadPosition]);

  // Handle order submission
  const handleOrderSubmit = async (orderData) => {
    try {
      console.log('🔍 DEBUG: Submitting order');
      console.log('🔍 DEBUG: Order data:', JSON.stringify(orderData, null, 2));
      console.log('🔍 DEBUG: Order user_id:', orderData.user_id, 'type=', typeof orderData.user_id, 'length=', orderData.user_id?.length);
      console.log('🔍 DEBUG: Current userId state:', userId, 'type=', typeof userId, 'length=', userId?.length);
      console.log('🔍 DEBUG: WebSocket status:', activeOrdersWsStatus);
      console.log('🔍 DEBUG: WebSocket readyState:', activeOrdersSocket?.readyState);
      
      setStatus('⏳ Submitting order...');
      
      
      const result = await fetchJSON(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        body: JSON.stringify(orderData)
      });
      
      console.log('🔍 DEBUG: Order submission response:', result);
      
      // v2.3.2: Fixed status check to handle both success formats
      if (result && (result.success === true || result.status === 'success')) {
        setStatus('✅ Order submitted successfully');
        console.log('🔍 DEBUG: Order submitted successfully, waiting for WebSocket update');
        console.log('🔍 DEBUG: Current active orders count:', activeOrders.orders.length);
        // Refresh position after order submission (position may change if order executes immediately)
        setTimeout(() => loadPosition(), 1000);
        // Don't force refresh - let WebSocket handle the update
        // This prevents flashing while maintaining real-time updates
      } else {
        setStatus(`❌ Order failed: ${result?.message || 'Unknown error'}`);
        console.error('🔍 DEBUG: Order submission failed:', result);
      }
      
      return result;
    } catch (error) {
      const errorMsg = `❌ Submit failed: ${error.message}`;
      console.error('🔍 DEBUG: Order submission error:', error);
      setStatus(errorMsg);
      return { success: false, message: error.message };
    }
  };

  // Handle order cancellation - let market data updates control UI
  const handleCancelOrder = async (orderId, userId, symbol = null) => {
    try {
      console.log('🔍 [CANCEL DEBUG] Starting cancel order:', { orderId, userId, symbol, defaultInstrument });
      console.log('🔍 [CANCEL DEBUG] Active orders count:', activeOrders.orders.length);
      console.log('🔍 [CANCEL DEBUG] Active orders IDs:', activeOrders.orders.map(o => ({ id: o.order_id, symbol: o.symbol, status: o.status })));
      
      setStatus('⏳ Canceling order...');
      
      // Get symbol from order if not provided - search in ALL orders, not filtered
      let orderSymbol = symbol;
      if (!orderSymbol) {
        const order = activeOrders.orders.find(o => o.order_id === orderId);
        if (order) {
          orderSymbol = order.symbol;
          console.log(`🔍 [CANCEL DEBUG] Found order ${orderId} in active orders, symbol: ${orderSymbol}`);
        } else {
          console.warn(`⚠️ [CANCEL DEBUG] Order ${orderId} not found in active orders state. Proceeding with cancel request anyway.`);
          // Don't block - order might be filtered or state might be stale
        }
      }
      
      // Add symbol as query parameter for instrument-aware routing
      const url = orderSymbol 
        ? `${API_BASE_URL}/api/orders/${orderId}?symbol=${orderSymbol}`
        : `${API_BASE_URL}/api/orders/${orderId}`;
      
      console.log(`🔍 [CANCEL DEBUG] Canceling order ${orderId} with URL: ${url}`);
      console.log(`🔍 [CANCEL DEBUG] Using symbol: ${orderSymbol || 'none'} (provided: ${symbol || 'none'}, default: ${defaultInstrument || 'none'})`);
      
      const result = await fetchJSON(url, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId })
      });
      
      console.log('🔍 [CANCEL DEBUG] Cancel response:', result);
      
      if (result.status === 'success' || result.success === true) {
        setStatus('✅ Order canceled successfully');
        // Refresh position after cancellation (in case position changed)
        setTimeout(() => loadPosition(), 1000);
        // Let WebSocket market data updates handle the UI changes
        return result;
      } else {
        setStatus(`❌ Cancel failed: ${result.message || result.reason || 'Unknown error'}`);
        return { success: false, message: result.message || result.reason };
      }
      
    } catch (error) {
      const errorMsg = `❌ Cancel failed: ${error.message}`;
      console.error('❌ [CANCEL DEBUG] Cancel order error:', error);
      console.error('❌ [CANCEL DEBUG] Error details:', {
        message: error.message,
        stack: error.stack,
        orderId,
        userId,
        symbol
      });
      setStatus(errorMsg);
      return { success: false, message: error.message };
    }
  };

  // Handle cancel all orders - let market data updates control UI
  const handleCancelAllOrders = async (userId) => {
    try {
      console.log('🔍 [CANCEL_ALL_DEBUG] Starting cancel all orders for user:', userId);
      console.log('🔍 [CANCEL_ALL_DEBUG] Visible instruments:', visibleInstruments);
      console.log('🔍 [CANCEL_ALL_DEBUG] Current order books:', Object.keys(orderBooksByInstrument));
      
      setStatus('⏳ Canceling all orders...');
      
      // Cancel orders for each visible instrument
      const cancelPromises = visibleInstruments.map(async (symbol) => {
        console.log(`🔍 [CANCEL_ALL_DEBUG] Canceling orders for instrument: ${symbol}`);
        try {
          const result = await fetchJSON(`${API_BASE_URL}/api/orders/user/${userId}?symbol=${symbol}`, {
            method: 'POST'
          });
          console.log(`🔍 [CANCEL_ALL_DEBUG] Cancel result for ${symbol}:`, result);
          return { symbol, result };
        } catch (error) {
          console.error(`🔍 [CANCEL_ALL_DEBUG] Error canceling orders for ${symbol}:`, error);
          return { symbol, error: error.message };
        }
      });
      
      const results = await Promise.all(cancelPromises);
      console.log('🔍 [CANCEL_ALL_DEBUG] All cancel results:', results);
      
      const totalCancelled = results.reduce((sum, r) => {
        if (r.result && r.result.status === 'success') {
          return sum + (r.result.cancelled_count || 0);
        }
        return sum;
      }, 0);
      
      const successCount = results.filter(r => r.result && r.result.status === 'success').length;
      console.log(`🔍 [CANCEL_ALL_DEBUG] Total cancelled: ${totalCancelled}, Success count: ${successCount}/${visibleInstruments.length}`);
      
      if (successCount > 0) {
        setStatus(`✅ Successfully cancelled ${totalCancelled} orders across ${successCount} instrument(s)`);
        // Refresh position after cancel all
        setTimeout(() => loadPosition(), 1000);
        // Force reload order books for all visible instruments
        console.log('🔍 [CANCEL_ALL_DEBUG] Reloading order books for all visible instruments...');
        visibleInstruments.forEach(symbol => {
          console.log(`🔍 [CANCEL_ALL_DEBUG] Reloading order book for ${symbol}`);
          loadBookForInstrument(symbol);
        });
        return { status: 'success', cancelled_count: totalCancelled };
      } else {
        const errorMsg = results.find(r => r.error)?.error || 'Failed to cancel orders';
        setStatus(`❌ Cancel all failed: ${errorMsg}`);
        return { success: false, message: errorMsg };
      }
      
    } catch (error) {
      const errorMsg = `❌ Cancel all failed: ${error.message}`;
      console.error('🔍 [CANCEL_ALL_DEBUG] Exception:', error);
      setStatus(errorMsg);
      return { success: false, message: error.message };
    }
  };

  // Handle order modification
  const handleModifyOrder = async (order, event) => {
    const rect = event.target.getBoundingClientRect();
    
    // Calculate smart positioning to keep modal within viewport
    const modalWidth = 400; // Approximate modal width
    const modalHeight = 300; // Approximate modal height
    const padding = 20; // Padding from edges
    
    let x = rect.left;
    let y = rect.bottom + 10;
    
    // Adjust horizontal position if modal would go off-screen
    if (x + modalWidth > window.innerWidth - padding) {
      x = window.innerWidth - modalWidth - padding;
    }
    if (x < padding) {
      x = padding;
    }
    
    // Adjust vertical position if modal would go off-screen
    if (y + modalHeight > window.innerHeight - padding) {
      y = rect.top - modalHeight - 10; // Position above the button instead
    }
    
    setModifyModal({ 
      show: true, 
      order: order, 
      position: { x, y } 
    });
  };

  // Handle modify modal submission
  const handleModifySubmit = async (newPrice, newQuantity) => {
    const order = modifyModal.order;
    if (!order) {
      console.error('❌ Modify order: No order provided');
      setStatus('❌ No order selected');
      setModifyModal({ show: false, order: null });
      return;
    }

    try {
      setStatus('⏳ Modifying order...');
      
      // Verify order exists in active orders (check all orders, not just filtered)
      const orderExists = activeOrders.orders.some(o => o.order_id === order.order_id);
      if (!orderExists) {
        console.warn(`⚠️ Order ${order.order_id} not found in active orders. Proceeding anyway - order may be filtered.`);
        // Don't block - order might be filtered by instrument filter
      }
      
      if (newPrice === order.price && newQuantity === order.remaining_quantity) {
        setStatus('❌ No changes made');
        setModifyModal({ show: false, order: null });
        return;
      }
      
      // Add symbol parameter for instrument-aware routing
      const symbol = order.symbol;
      const url = symbol 
        ? `${API_BASE_URL}/api/orders/${order.order_id}/modify?symbol=${symbol}`
        : `${API_BASE_URL}/api/orders/${order.order_id}/modify`;
      
      const result = await fetchJSON(url, {
        method: 'PUT',
        body: JSON.stringify({
          user_id: order.user_id,
          new_price: newPrice,
          new_quantity: newQuantity,
          client_order_id: order.client_order_id  // Include original client order ID
        })
      });
      
      if (result.status === 'success') {
        setStatus(`✅ Order modified successfully: ${result.message}`);
        setModifyModal({ show: false, order: null, position: { x: 0, y: 0 } });
        
        // Optimistic UI update - refresh order book immediately
        loadBook();
        
        return result;
      } else {
        setStatus(`❌ Modify failed: ${result.message}`);
        return { success: false, message: result.message };
      }
      
    } catch (error) {
      const errorMsg = `❌ Modify failed: ${error.message}`;
      setStatus(errorMsg);
      return { success: false, message: error.message };
    }
  };

  // Close modify modal
  const closeModifyModal = () => {
    setModifyModal({ show: false, order: null, position: { x: 0, y: 0 } });
  };

  // Check instrument access before showing trading interface
  useEffect(() => {
    const checkInstrumentAccess = async () => {
      // Only check if user is authenticated, instrument is loaded, and we haven't checked yet
      if (!isAuthenticated || !userId || !defaultInstrument || hasAccess !== null) {
        return;
      }

      // Get Clerk user ID (not email username) for access check
      // Use clerkUserId from useClerkAuth hook (works for both Clerk SDK and session-based auth)
      if (!clerkUserId) {
        // If Clerk user ID not available, skip access check (fallback)
        console.warn('App: Clerk user ID not available, skipping access check');
        setHasAccess(true); // Allow access if we can't check (backward compatibility)
        return;
      }

      setAccessCheckLoading(true);
      setAccessCheckError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/instrument-access/check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clerk_user_id: clerkUserId,
            instrument_symbol: defaultInstrument
          })
        });

        if (!response.ok) {
          throw new Error(`Access check failed: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.has_access) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
          setAccessCheckError(`You don't have access to trade ${defaultInstrument}. Please contact support to request access.`);
        }
      } catch (error) {
        console.error('App: Error checking instrument access:', error);
        // On error, allow access (fail open) but log the error
        setHasAccess(true);
        setAccessCheckError(null);
      } finally {
        setAccessCheckLoading(false);
      }
    };

    checkInstrumentAccess();
  }, [isAuthenticated, userId, defaultInstrument, hasAccess, API_BASE_URL]);

  // Redirect to marketing site if not authenticated
  // Give Clerk extra time to initialize before redirecting
  useEffect(() => {
    // Don't redirect if we're still processing a session_id
    if (isProcessingSession) {
      return;
    }
    
    // Check if session_id is in URL - if so, wait longer before redirecting
    const urlParams = new URLSearchParams(window.location.search);
    const hasSessionId = urlParams.has('session_id') || urlParams.has('sid');
    
    if (!authLoading && (!isAuthenticated || !userId)) {
      // If session_id is in URL, wait longer (15 seconds) to allow backend verification
      // Otherwise, wait 10 seconds (increased from 5)
      const delay = hasSessionId ? 15000 : 10000;
      
      const redirectTimer = setTimeout(() => {
        // Don't redirect if we're processing session or if auth succeeded
        if (isProcessingSession || isAuthenticated) {
          return;
        }
        
        // Double-check one more time before redirecting
        const finalCheck = new URLSearchParams(window.location.search);
        const finalSessionId = finalCheck.get('session_id') || finalCheck.get('sid');
        
        if (!finalSessionId && (!isAuthenticated || !userId)) {
          window.location.href = 'https://amberhorizon.io/';
        }
      }, delay);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [isAuthenticated, userId, authLoading, isProcessingSession]);

  // Show loading state while auth, instruments, or access check are loading
  if (authLoading || instrumentsLoading || accessCheckLoading || hasAccess === null || isProcessingSession) {
    return (
      <div className="trading-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div style={{textAlign: 'center'}}>
          <h2>Loading...</h2>
          <p style={{color: 'var(--muted)'}}>
            {isProcessingSession ? 'Verifying session...' :
             authLoading ? 'Checking authentication...' : 
             instrumentsLoading ? 'Connecting to trading system...' : 
             accessCheckLoading ? 'Verifying access...' : 
             'Initializing...'}
          </p>
        </div>
      </div>
    );
  }

  // Don't render trading interface if not authenticated (redirect will happen)
  if (!isAuthenticated || !userId) {
    return null;
  }

  // Show error state if instruments failed to load
  if (instrumentsError) {
    return (
      <div className="trading-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div style={{textAlign: 'center'}}>
          <h2>❌ Failed to load instruments</h2>
          <p style={{color: 'var(--muted)'}}>{instrumentsError}</p>
          <button onClick={() => window.location.reload()} style={{marginTop: '20px', padding: '10px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show error state if access denied
  if (hasAccess === false) {
    return (
      <div className="trading-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div style={{textAlign: 'center', maxWidth: '500px', padding: '20px'}}>
          <h2>❌ Access Denied</h2>
          <p style={{color: 'var(--muted)', marginBottom: '20px'}}>
            {accessCheckError || `You don't have access to trade ${defaultInstrument}.`}
          </p>
          <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
            <button 
              onClick={() => window.location.href = 'https://amberhorizon.io/dashboard'} 
              style={{padding: '10px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}
            >
              Go to Dashboard
            </button>
            <button 
              onClick={() => window.location.reload()} 
              style={{padding: '10px 20px', background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer'}}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trading-container">
      
      <header style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'linear-gradient(180deg,#141838,#0f1220)', position: 'sticky', top: 0, zIndex: 5}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <a href="https://amberhorizon.io" target="_blank" rel="noopener noreferrer">
            <img src="/horizon_logo.png" alt="AmberHorizon logo" style={{maxHeight: '60px', maxWidth: '300px', width: 'auto', height: 'auto', objectFit: 'contain', cursor: 'pointer', transition: 'opacity 0.2s ease'}}/>
          </a>
        </div>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          <div style={{fontSize: '10px', color: 'var(--muted)', display: 'flex', gap: '8px', alignItems: 'center'}}>
            <span>Last update: {lastUpdate}</span>
            <span>WS: <span className={`ws-status ${wsStatus}`} style={{color: wsStatus === 'live' ? '#4ade80' : 'var(--muted)'}}>{wsStatus}</span></span>
          </div>
          {userEmail && (
            <div style={{fontSize: '12px', color: 'var(--muted)', padding: '6px 10px', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '6px'}}>
              {userEmail}
            </div>
          )}
          <label style={{fontSize: '12px', color: 'var(--muted)'}}>Depth</label>
          <select 
            value={depth} 
            onChange={(e) => setDepth(Number(e.target.value))}
            style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '10px'}}
          >
            <option value={10}>Top 10</option>
            <option value={25}>Top 25</option>
            <option value={0}>All</option>
          </select>
          <button 
            onClick={signOut}
            style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'}}
          >
            Sign Out
          </button>
          <a className="btn" href="/admin" rel="nofollow" style={{background: 'var(--accent)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '6px', textDecoration: 'none', fontSize: '13px'}}>Admin</a>
        </div>
      </header>

      {/* Market Ticker - Full Width */}
      <div style={{padding: '12px 12px 0 12px'}}>
        <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
          <div className="content" style={{padding: '0'}}>
            <MarketTicker 
              wsUrl={`${WS_BASE_URL}/ws/market_data`}
              restUrl={`${API_BASE_URL}/api/order_book`}
              speedSeconds={50}
              allInstruments={allInstrumentsList}
            />
          </div>
        </div>
      </div>

      <main className="grid" style={{display: 'grid', gap: '12px', padding: '12px', gridTemplateColumns: '1.3fr 1fr'}}>
        <section className="col" style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {/* Instrument Depth Panels */}
          {visibleInstruments.map((symbol) => {
            const orderBook = orderBooksByInstrument[symbol] || { bids: [], asks: [], last_traded_price: null };
            const filteredBids = depth > 0 ? orderBook.bids.slice(0, depth) : orderBook.bids;
            const filteredAsks = depth > 0 ? orderBook.asks.slice(0, depth) : orderBook.asks;
            const lastTradedPrice = orderBook.last_traded_price !== null && typeof orderBook.last_traded_price === 'number' 
              ? orderBook.last_traded_price.toFixed(2) 
              : null;
            
            return (
              <div key={symbol} className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border)'}}>
                  <h3 style={{margin: 0, fontSize: '12px', color: 'var(--muted)'}}>{symbol} Depth</h3>
                  {visibleInstruments.length > 1 && (
                    <button
                      onClick={() => removeInstrument(symbol)}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title="Remove instrument"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="content" style={{padding: '12px'}}>
                  <OrderBook 
                    bids={filteredBids}
                    asks={filteredAsks}
                    onOrderClick={handleOrderSubmit}
                    onCancelOrder={handleCancelOrder}
                    onModifyOrder={handleModifyOrder}
                    instrument={symbol}
                    tickSize={getTickSize(symbol)}
                    validatePrice={validatePrice}
                    roundToTick={roundToTick}
                    lastTradedPrice={lastTradedPrice}
                    userId={userId}
                  />
                </div>
              </div>
            );
          })}
          
          {/* Add Instrument Selector */}
          <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
            <div className="content" style={{padding: '12px', display: 'flex', alignItems: 'center', gap: '8px'}}>
              <label style={{fontSize: '12px', color: 'var(--muted)', whiteSpace: 'nowrap'}}>Add Instrument:</label>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    addInstrument(e.target.value);
                    e.target.value = '';
                  }
                }}
                style={{
                  width: '100%',
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  flex: 1
                }}
              >
                <option value="">Select instrument to add...</option>
                {getSymbols && getSymbols().filter(symbol => !visibleInstruments.includes(symbol)).map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
            <h3 style={{margin: 0, padding: '12px 14px', borderBottom: '1px solid var(--border)', fontSize: '14px', color: 'var(--muted)'}}>Order Entry</h3>
            <div className="content" style={{padding: '12px'}}>
              <OrderForm 
                onSubmit={handleOrderSubmit}
                onCancelAll={handleCancelAllOrders}
                instrument={selectedOrderInstrument || defaultInstrument}
                tickSize={getTickSize(selectedOrderInstrument || defaultInstrument)}
                validatePrice={validatePrice}
                roundToTick={roundToTick}
                userId={userId}
                visibleInstruments={visibleInstruments}
                selectedOrderInstrument={selectedOrderInstrument}
                setSelectedOrderInstrument={setSelectedOrderInstrument}
              />
            </div>
          </div>
        </section>

        <section className="col" style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border)'}}>
              <h3 style={{margin: 0, fontSize: '14px', color: 'var(--muted)'}}>Active Orders</h3>
              <select
                value={activeOrdersFilter}
                onChange={(e) => setActiveOrdersFilter(e.target.value)}
                style={{
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              >
                <option value="ALL">All Instruments</option>
                {getSymbols && getSymbols().map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </select>
            </div>
            <div className="content" style={{padding: '12px'}}>
              <ActiveOrders 
                userId={userId} 
                activeOrders={{
                  orders: activeOrdersFilter === 'ALL' 
                    ? activeOrders.orders 
                    : activeOrders.orders.filter(order => order.symbol === activeOrdersFilter),
                  timestamp: activeOrders.timestamp
                }} 
                onCancelOrder={handleCancelOrder}
                onModifyOrder={handleModifyOrder}
              />
            </div>
          </div>

          <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
            <div className="content" style={{padding: '12px'}}>
              <Positions position={position} symbol={defaultInstrument} />
            </div>
          </div>

          <div className="card" style={{background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden'}}>
            <h3 style={{margin: 0, padding: '12px 14px', borderBottom: '1px solid var(--border)', fontSize: '14px', color: 'var(--muted)'}}>Recent Matches</h3>
            <div className="content" style={{padding: '12px'}}>
              <Matches matches={matches} />
            </div>
          </div>
        </section>
      </main>

      {/* Trading Chart - Full Width */}
      <div style={{padding: '0 12px 12px 12px'}}>
        <TradingChart 
          data={matches}
          availableInstruments={availableInstruments}
          selectedInstrument={selectedChartInstrument}
          onInstrumentChange={setSelectedChartInstrument}
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
        />
      </div>
      
      {/* Status bar at bottom of screen */}
      {status && (
        <div 
          className={`status ${status.includes('✅') ? 'ok' : status.includes('❌') ? 'error' : 'warn'}`} 
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '10px 16px',
            background: 'var(--panel)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            zIndex: 1000,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {status}
        </div>
      )}
      
      {/* Version footer */}
      <footer style={{position: 'fixed', bottom: status ? '40px' : 0, right: 0, padding: '4px 8px', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '6px 0 0 0', fontSize: '10px', color: 'var(--muted)', zIndex: 1000}}>
        v{FRONTEND_VERSION}
      </footer>

      {/* Modify Order Modal */}
      {modifyModal.show && (
        <ModifyOrderModal
          order={modifyModal.order}
          onSubmit={handleModifySubmit}
          onCancel={closeModifyModal}
          instrument={defaultInstrument}
          tickSize={getTickSize(defaultInstrument)}
          validatePrice={validatePrice}
          roundToTick={roundToTick}
          position={modifyModal.position}
        />
      )}
    </div>
  );
}

// Modify Order Modal Component
const ModifyOrderModal = ({ order, onSubmit, onCancel, instrument, tickSize, validatePrice, roundToTick, position }) => {
  const [newPrice, setNewPrice] = useState(order?.price?.toString() || '');
  const [newQuantity, setNewQuantity] = useState(order?.remaining_quantity?.toString() || '');
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const priceValue = parseFloat(newPrice);
    const quantityValue = parseFloat(newQuantity);
    
    // Validation
    const newErrors = {};
    if (!newPrice || isNaN(priceValue) || priceValue <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }
    if (!newQuantity || isNaN(quantityValue) || quantityValue <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }
    if (priceValue && !validatePrice(instrument, priceValue)) {
      newErrors.price = `Price must be a multiple of tick size ${tickSize}`;
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    onSubmit(priceValue, quantityValue);
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    setNewPrice(value);
    if (errors.price) {
      setErrors(prev => ({ ...prev, price: null }));
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setNewQuantity(value);
    if (errors.quantity) {
      setErrors(prev => ({ ...prev, quantity: null }));
    }
  };

  if (!order) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.3)',
      zIndex: 2000
    }}>
      <div style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '0',
        minWidth: '400px',
        maxWidth: '500px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        zIndex: 2001
      }}>
        {/* Header */}
        <div style={{
          background: 'var(--accent)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px 12px 0 0',
          fontSize: '16px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            ✏️ Modify Order
          </div>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px', position: 'relative' }}>
          <button
            onClick={onCancel}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--muted)',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'var(--panel)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
            title="Close"
          >
            ×
          </button>
          <div style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--muted)' }}>
          <div><strong>Order ID:</strong> {order.order_id.substring(0, 8)}...</div>
          <div><strong>Side:</strong> {order.side.toUpperCase()}</div>
          <div><strong>Current Price:</strong> {order.price}</div>
          <div><strong>Current Quantity:</strong> {order.remaining_quantity}</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
              New Price *
            </label>
            <input
              type="number"
              step={tickSize}
              value={newPrice}
              onChange={handlePriceChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.price ? 'var(--error)' : 'var(--border)'}`,
                borderRadius: '6px',
                background: 'var(--background)',
                color: 'var(--text)',
                fontSize: '14px'
              }}
              placeholder={`Enter new price (tick size: ${tickSize})`}
            />
            {errors.price && (
              <div style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>
                {errors.price}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
              New Quantity *
            </label>
            <input
              type="number"
              step="1"
              value={newQuantity}
              onChange={handleQuantityChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.quantity ? 'var(--error)' : 'var(--border)'}`,
                borderRadius: '6px',
                background: 'var(--background)',
                color: 'var(--text)',
                fontSize: '14px'
              }}
              placeholder="Enter new quantity"
            />
            {errors.quantity && (
              <div style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>
                {errors.quantity}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                background: 'transparent',
                color: 'var(--muted)',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: '1px solid var(--accent)',
                borderRadius: '6px',
                background: 'var(--accent)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Modify Order
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default App;

// Version for cache busting
export const APP_VERSION = FRONTEND_VERSION;